import React, { useMemo, useState } from 'react';
import Icon from '../hr/Icon';

// A user's own permission matrix (User Access Control › create / edit user), laid out like the roles' matrix: modules down
// the side, the common actions across (View, Create, Edit, Delete, Approve, Export), a module's own actions under Other
// (expand the module to see them one by one) and All. A tick comes from the user's roles unless the administrator revoked it
// for this user, or it is granted to this user although none of their roles has it. Only the differences are saved
// (backend/app/services/user_permission_service.py).
//
// rolePerms  { permission: [role names that give it] }      grants / revokes  Set of permissions
// onChange({ grants, revokes })                              taxonomy  [{ key, label, actions: [{ key, label }] }]

const COLS = [
  { key: 'view', label: 'View', actions: ['view'] },
  { key: 'create', label: 'Create', actions: ['create', 'add'] },
  { key: 'edit', label: 'Edit', actions: ['edit'] },
  { key: 'delete', label: 'Delete', actions: ['delete'] },
  { key: 'approve', label: 'Approve', actions: ['approve'] },
  { key: 'export', label: 'Export', actions: ['export'] },
];
const STD = new Set(COLS.flatMap(c => c.actions));
// the app's main modules, as the roles' matrix nests sub-modules under them (a permission module lands in the first
// parent whose test matches its key; anything new falls under "Other modules")
const PARENTS = [
  { key: 'command', label: 'Command Center', icon: 'gauge', has: k => k === 'dashboard' },
  { key: 'cost', label: 'Cost Control', icon: 'chart-pie', has: k => ['cost_control', 'boq_hub', 'qs_hub', 'expense_master'].includes(k) },
  { key: 'inventory', label: 'Inventory & Procurement', icon: 'package', has: k => ['material_request', 'material_po'].includes(k) },
  { key: 'requisition', label: 'Enterprise Requisition', icon: 'shopping-cart', has: k => k === 'enterprise_requisition' },
  { key: 'voucher', label: 'Payment Voucher', icon: 'credit-card', has: k => k === 'payment_voucher' },
  { key: 'hr', label: 'HR & People', icon: 'users', has: k => ['hr_people', 'hr_requests', 'adv_requests', 'hr_expenses'].includes(k) },
  { key: 'payroll', label: 'Payroll & Compensation', icon: 'wallet', has: k => k === 'payroll' },
  { key: 'reports', label: 'Cost Reports', icon: 'chart-column', has: k => k === 'reports' },
  { key: 'admin', label: 'System Administration', icon: 'shield-check', has: k => k.startsWith('admin_') },
];
const TONE = {
  role: { bg: '#eff6ff', border: '#93c5fd', fg: '#1d4ed8', label: 'From role' },
  granted: { bg: '#dcfce7', border: '#4ade80', fg: '#15803d', label: 'Granted to this user' },
  revoked: { bg: '#ffedd5', border: '#fb923c', fg: '#c2410c', label: 'Revoked (role gives it)' },
};
const NAVY = '#0b318b';

function Box({ state, disabled, onChange, title }) {   // state: true | false | 'mixed' | undefined (no such permission)
  if (state === undefined) return <span style={{ color: '#cbd5e1' }}>—</span>;
  return (
    <input type="checkbox" title={title} disabled={disabled} checked={state === true}
           ref={el => { if (el) el.indeterminate = state === 'mixed'; }}
           onChange={e => onChange(e.target.checked)} style={{ width: 16, height: 16, accentColor: NAVY, cursor: disabled ? 'not-allowed' : 'pointer' }} />
  );
}

export default function UserPermissionMatrix({ taxonomy, rolePerms, grants, revokes, onChange, locked, isSuper }) {
  const [open, setOpen] = useState(() => new Set());
  const [q, setQ] = useState('');
  const [onlyOn, setOnlyOn] = useState(false);

  const has = p => (rolePerms[p] && !revokes.has(p)) || grants.has(p);
  const stateOf = p => (rolePerms[p] ? (revokes.has(p) ? 'revoked' : 'role') : grants.has(p) ? 'granted' : null);
  const triState = ps => { if (!ps.length) return undefined; const n = ps.filter(has).length; return n === 0 ? false : n === ps.length ? true : 'mixed'; };
  const setMany = (ps, on) => {
    const g = new Set(grants), r = new Set(revokes);
    ps.forEach(p => { if (rolePerms[p]) { if (on) r.delete(p); else r.add(p); } else if (on) g.add(p); else g.delete(p); });
    onChange({ grants: g, revokes: r });
  };

  // sub-modules under the app's main modules; a parent with one sub-module is that module's own row, with the parent's
  // name and icon
  const tree = useMemo(() => {
    const placed = new Set();
    const out = PARENTS.map(pt => {
      const children = taxonomy.filter(m => !placed.has(m.key) && pt.has(m.key));
      children.forEach(m => placed.add(m.key));
      return { ...pt, children };
    });
    const rest = taxonomy.filter(m => !placed.has(m.key));
    if (rest.length) out.push({ key: 'other', label: 'Other modules', icon: 'layout-grid', children: rest });
    return out.filter(g => g.children.length)
      .map(g => (g.children.length === 1 ? { module: { ...g.children[0], label: g.label, icon: g.icon } } : { group: g }));
  }, [taxonomy]);

  const permsOf = m => m.actions.map(a => `${m.key}:${a.key}`);
  const colPerms = (m, c) => m.actions.filter(a => c.actions.includes(a.key)).map(a => `${m.key}:${a.key}`);
  const otherActs = m => m.actions.filter(a => !STD.has(a.key));
  const allModules = taxonomy;
  const matches = m => (!q || m.label.toLowerCase().includes(q.toLowerCase()) || m.actions.some(a => a.label.toLowerCase().includes(q.toLowerCase())))
    && (!onlyOn || permsOf(m).some(has));
  const count = allModules.reduce((n, m) => n + permsOf(m).filter(has).length, 0);
  const toggle = k => setOpen(s => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });

  if (isSuper) {
    return <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>A Super Administrator has every permission; there is nothing to grant or revoke.</div>;
  }

  const th = { padding: '10px 8px', color: '#fff', fontSize: 11, fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase', textAlign: 'center',
    borderLeft: '1px solid rgba(255,255,255,.15)', whiteSpace: 'nowrap', position: 'sticky', top: 0, background: NAVY, zIndex: 1 };
  const td = { padding: '8px', textAlign: 'center', borderTop: '1px solid var(--border)', borderLeft: '1px solid var(--border)' };
  const cellBg = ps => {   // a cell takes its tint from the overrides in it (granted / revoked), else from its roles
    const st = ps.map(stateOf);
    if (st.includes('revoked')) return TONE.revoked.bg;
    if (st.includes('granted')) return TONE.granted.bg;
    return st.includes('role') ? TONE.role.bg : 'transparent';
  };
  const why = ps => ps.map(p => {
    const st = stateOf(p);
    return st === 'role' ? `${p}: from ${rolePerms[p].join(', ')}` : st === 'revoked' ? `${p}: revoked (${rolePerms[p].join(', ')} gives it)`
      : st === 'granted' ? `${p}: granted to this user` : `${p}: not granted`;
  }).join('\n');

  const moduleRow = (m, depth) => {
    const others = otherActs(m);
    const isOpen = open.has(m.key) || (!!q && others.length > 0);
    return (
      <React.Fragment key={m.key}>
        <tr style={{ background: depth ? 'var(--bg-card)' : 'color-mix(in srgb, var(--accent) 5%, var(--bg-card))' }}>
          <td style={{ ...td, textAlign: 'left', borderLeft: 'none', paddingLeft: 12 + depth * 22 }}>
            <button type="button" onClick={() => others.length && toggle(m.key)}
                    style={{ background: 'none', border: 'none', padding: 0, cursor: others.length ? 'pointer' : 'default', fontSize: 13, fontWeight: depth ? 600 : 700,
                      color: 'var(--text-primary)', textAlign: 'left' }}>
              <span style={{ display: 'inline-block', width: 14, color: 'var(--text-muted)' }}>{others.length ? (isOpen ? '▾' : '▸') : ''}</span>
              {m.icon && <Icon name={m.icon} size={15} style={{ verticalAlign: '-2px', marginRight: 7, color: NAVY }} />}
              {m.label.replace(/^Admin · /, '')}
              <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 500, color: 'var(--text-muted)' }}>· {permsOf(m).filter(has).length}/{m.actions.length}</span>
            </button>
          </td>
          {COLS.map(c => { const ps = colPerms(m, c); return (
            <td key={c.key} style={{ ...td, background: cellBg(ps) }} title={ps.length ? why(ps) : undefined}>
              <Box state={triState(ps)} disabled={locked} onChange={on => setMany(ps, on)} />
            </td>); })}
          {(() => { const ps = others.map(a => `${m.key}:${a.key}`); return (
            <td style={{ ...td, background: cellBg(ps) }} title={ps.length ? why(ps) : undefined}>
              <Box state={triState(ps)} disabled={locked} onChange={on => setMany(ps, on)} title={ps.length ? `${ps.length} module-specific permission(s) — expand the module to set them one by one` : undefined} />
            </td>); })()}
          <td style={{ ...td, background: 'var(--bg-elevated)' }}><Box state={triState(permsOf(m))} disabled={locked} onChange={on => setMany(permsOf(m), on)} title="Every permission in this module" /></td>
        </tr>
        {isOpen && others.map(a => {
          const p = `${m.key}:${a.key}`;
          const st = stateOf(p);
          return (
            <tr key={p}>
              <td style={{ ...td, textAlign: 'left', borderLeft: 'none', paddingLeft: 40 + depth * 22, fontSize: 12.5, color: 'var(--text-secondary)' }}>
                {a.label}
                {st && <span style={{ marginLeft: 8, fontSize: 10.5, fontWeight: 700, color: TONE[st].fg }}>
                  {st === 'role' ? rolePerms[p].join(', ') : st === 'revoked' ? 'Revoked' : 'Granted'}</span>}
              </td>
              {COLS.map(c => <td key={c.key} style={td} />)}
              <td style={{ ...td, background: cellBg([p]) }} title={why([p])}><Box state={has(p)} disabled={locked} onChange={on => setMany([p], on)} /></td>
              <td style={td} />
            </tr>
          );
        })}
      </React.Fragment>
    );
  };

  const groupRow = gr => {
    const kids = gr.children.filter(matches);
    if (!kids.length) return null;
    const isOpen = open.has(gr.key) || !!q;
    const ps = kids.flatMap(permsOf);
    return (
      <React.Fragment key={gr.key}>
        <tr style={{ background: 'color-mix(in srgb, var(--accent) 5%, var(--bg-card))' }}>
          <td style={{ ...td, textAlign: 'left', borderLeft: 'none', paddingLeft: 12 }}>
            <button type="button" onClick={() => toggle(gr.key)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
              <span style={{ display: 'inline-block', width: 14, color: 'var(--text-muted)' }}>{isOpen ? '▾' : '▸'}</span>
              <Icon name={gr.icon} size={15} style={{ verticalAlign: '-2px', marginRight: 7, color: NAVY }} />{gr.label}
              <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 500, color: 'var(--text-muted)' }}>· {ps.filter(has).length}/{ps.length}</span>
            </button>
          </td>
          {COLS.map(c => { const cps = kids.flatMap(m => colPerms(m, c)); return (
            <td key={c.key} style={{ ...td, background: cellBg(cps) }}><Box state={triState(cps)} disabled={locked} onChange={on => setMany(cps, on)} /></td>); })}
          {(() => { const ops = kids.flatMap(m => otherActs(m).map(a => `${m.key}:${a.key}`)); return (
            <td style={{ ...td, background: cellBg(ops) }}><Box state={triState(ops)} disabled={locked} onChange={on => setMany(ops, on)} /></td>); })()}
          <td style={{ ...td, background: 'var(--bg-elevated)' }}><Box state={triState(ps)} disabled={locked} onChange={on => setMany(ps, on)} /></td>
        </tr>
        {isOpen && kids.map(m => moduleRow(m.label === gr.label ? { ...m, label: `${m.label} — module access` } : m, 1))}
      </React.Fragment>
    );
  };

  const visible = tree.filter(n => (n.module ? matches(n.module) : n.group.children.some(matches)));
  const shownModules = visible.flatMap(n => (n.module ? [n.module] : n.group.children.filter(matches)));
  const colHead = c => { const ps = shownModules.flatMap(m => colPerms(m, c)); return <Box state={triState(ps)} disabled={locked} onChange={on => setMany(ps, on)} title={`${c.label} for every module shown`} />; };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginBottom: 10 }}>
        <input placeholder="Filter modules or permissions…" value={q} onChange={e => setQ(e.target.value)}
               style={{ flex: '0 1 280px', padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12.5, background: 'var(--bg-elevated)', color: 'var(--text-primary)' }} />
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--text-secondary)', cursor: 'pointer' }}>
          <input type="checkbox" checked={onlyOn} onChange={e => setOnlyOn(e.target.checked)} /> Only granted
        </label>
        {Object.entries(TONE).map(([k, t]) => (
          <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: 'var(--text-secondary)' }}>
            <span style={{ width: 12, height: 12, borderRadius: 3, background: t.bg, border: `1.5px solid ${t.border}` }} />{t.label}
          </span>
        ))}
        <span style={{ marginLeft: 'auto', fontSize: 11.5, color: 'var(--text-muted)' }}>
          {count} permission{count === 1 ? '' : 's'} · {grants.size} granted · {revokes.size} revoked
        </span>
      </div>
      <div style={{ border: '1px solid var(--border)', borderRadius: 10, maxHeight: 520, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, minWidth: 820 }}>
          <thead>
            <tr>
              <th style={{ ...th, textAlign: 'left', borderLeft: 'none', paddingLeft: 14 }}>Module / Sub-module / Permission</th>
              {COLS.map(c => <th key={c.key} style={th}>{c.label}<div style={{ marginTop: 6 }}>{colHead(c)}</div></th>)}
              <th style={th} title="Module-specific permissions (Submit, Return, Set-up, …) — expand a module to see them">Other</th>
              <th style={th}>All</th>
            </tr>
          </thead>
          <tbody>
            {visible.map(n => (n.module ? moduleRow(n.module, 0) : groupRow(n.group)))}
            {!visible.length && <tr><td colSpan={COLS.length + 3} style={{ padding: 16, fontSize: 12.5, color: 'var(--text-muted)' }}>No module matches.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
